import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { DialogClose, useAlerts } from '@qualifio/ui-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MutationKey, useMutation } from '@tanstack/react-query';
import { extractErrorMessage } from '@loyalty/shared-common';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { useTranslation } from 'react-i18next';

interface Props {
  validationValue: string;
  validationPlaceholder?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: string;
  description: string;
  callBack: () => Promise<any>;
  buttonText?: string;
  mutationKey: MutationKey;
}

export function TypeToConfirmModal({
  mutationKey,
  validationValue,
  validationPlaceholder,
  isOpen,
  setIsOpen,
  title,
  description,
  callBack,
  buttonText,
}: Props) {
  const { t } = useTranslation();
  const [typedValue, setTypedValue] = useState('');
  const { notify } = useAlerts();

  const onClose = () => setIsOpen(false);

  const { isPending, mutate } = useMutation({
    mutationKey,
    mutationFn: callBack,
    onError: (err: Error) => notify(extractErrorMessage(err) || 'Verification failed', 'error'),
  });

  useEffect(() => {
    if (isOpen) {
      setTypedValue('');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogClose close={onClose} />
      <DialogTitle>{title}</DialogTitle>
      <form
        onSubmit={event => {
          event.preventDefault();
          mutate();
        }}
      >
        <DialogContent sx={{ textAlign: 'start !important' }}>
          <p style={{ whiteSpace: 'break-spaces' }}>{description}</p>
          <TextField
            value={typedValue}
            placeholder={validationPlaceholder ?? validationValue}
            onChange={e => setTypedValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button color="grey" onClick={onClose} disabled={isPending}>
            {t('generic.actions.cancel')}
          </Button>
          <Button
            color="error"
            type="submit"
            startIcon={isPending && <FontAwesomeIcon icon={faSpinner} spin />}
            disabled={isPending || typedValue !== validationValue}
          >
            {buttonText ?? t('program.reset.dialog.confirm')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
